import { Injectable } from '@angular/core';
import { ApolloQueryResult } from 'apollo-client';
import gql from 'graphql-tag';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, map, shareReplay, switchMap, tap } from 'rxjs/operators';
import { AppSyncService } from '../core/appsync.service';
import { ClinicSetupService } from '../logged-in-navbar/clinic-setup-modal/clinic-setup.service';
import {
  createDaySheetTemplate,
  getDaySheetTemplateById,
  listDaySheetTemplatesByClinicSetupId,
  updateDaySheetTemplate,
} from '../../graphql/custom_queries';
import {
  CreateDaySheetTemplateInput,
  DaySheetEyeMode,
  DaySheetTemplate,
  DaySheetTemplateSign,
  ListDaySheetTemplatesByClinicSetupIdQuery,
  UpdateDaySheetTemplateInput,
} from './day-sheet.types';
import defaultTemplatesRaw from './default-day-sheet-templates.json';

export interface TemplateSignConfig {
  signId: string;
  label: string;
  eyeMode: DaySheetEyeMode;
  sortOrder: number;
}

export interface DefaultTemplateConfig {
  name: string;
  assessmentType: string | null;
  questionnaires: string[];
  signCount: number;
}

interface DefaultTemplateRaw {
  key: string;
  name: string;
  assessmentType: string | null;
  questionnaires: string[];
  signs: Array<{ signId: string; label: string; eyeMode: string }>;
}

const DEFAULT_TEMPLATES = defaultTemplatesRaw as DefaultTemplateRaw[];
const DEFAULT_TEMPLATE_NAMES = new Set(DEFAULT_TEMPLATES.map(d => d.name));

// Old names that were seeded before renaming. Maps old name → new { name, assessmentType }.
const OLD_DEFAULT_MIGRATION: Record<string, { name: string; assessmentType: string }> = {
  // Pre-assessment-rename names (original seeds)
  'Dry Eye \u2013 New Patient':      { name: 'Dry Eye New Patient',       assessmentType: 'DryEye' },
  'Dry Eye \u2013 Follow-up':        { name: 'Dry Eye Follow-up',         assessmentType: 'DryEye' },
  'Dry Eye \u2013 Specialized':      { name: 'Dry Eye Specialized',       assessmentType: 'DryEyeSpecialized' },
  // Post-assessment-rename names (intermediate migration)
  'Gold Standard':                   { name: 'Dry Eye New Patient',       assessmentType: 'DryEye' },
  'Accelerated Dry Eye Assessment':  { name: 'Dry Eye Specialized',       assessmentType: 'DryEyeSpecialized' },
  'Screening':                       { name: 'Cornea Consultation',       assessmentType: 'Cornea' },
  'Pre-Operative Screening':         { name: 'Refractive Surgery Consult', assessmentType: 'RefractiveSurgery' },
};

@Injectable({
  providedIn: 'root',
})
export class DaySheetTemplateService {
  // PHASE1_SIMPLIFY: the single template every account uses. Its signs are sourced
  // from local JSON (see getDefaultTemplate*) so the day sheet works even if backend
  // seeding hasn't completed or failed. Remove when users can configure templates.
  public static readonly DEFAULT_TEMPLATE_NAME = 'Dry Eye Specialized';

  private _defaultTemplateIds = new Set<string>();
  private seed$: Observable<DaySheetTemplate[]> | null = null;

  get defaultTemplateIds(): ReadonlySet<string> {
    return this._defaultTemplateIds;
  }

  get defaultTemplateConfigs(): DefaultTemplateConfig[] {
    return DEFAULT_TEMPLATES.map(d => ({
      name: d.name,
      assessmentType: d.assessmentType,
      questionnaires: d.questionnaires,
      signCount: d.signs.length,
    }));
  }

  constructor(
    private appSyncService: AppSyncService,
    private clinicSetupService: ClinicSetupService,
  ) {}

  // ─── Fixed default template (local source of truth) ───────────────────────────

  /** The raw JSON config for the fixed default template (never undefined). */
  private get defaultRaw(): DefaultTemplateRaw {
    return (
      DEFAULT_TEMPLATES.find(d => d.name === DaySheetTemplateService.DEFAULT_TEMPLATE_NAME) ||
      DEFAULT_TEMPLATES[0]
    );
  }

  /** Signs for the fixed default template, resolved locally. Never empty. */
  getDefaultTemplateSigns(): DaySheetTemplateSign[] {
    return this.defaultRaw.signs.map((s, i) => ({
      signId: s.signId,
      label: s.label,
      eyeMode: s.eyeMode as DaySheetEyeMode,
      sortOrder: i + 1,
    }));
  }

  /** Assessment type for the fixed default template, resolved locally. */
  getDefaultAssessmentType(): string | null {
    return this.defaultRaw.assessmentType;
  }

  /**
   * A DaySheetTemplate for the fixed default, synthesized from local JSON.
   * Pass the backend-seeded id (when known) so the DaySheetPatient link is
   * persisted; without it the dialog still works because signs come from JSON.
   */
  buildDefaultTemplate(id?: string): DaySheetTemplate {
    const d = this.defaultRaw;
    return {
      __typename: 'DaySheetTemplate',
      id,
      name: d.name,
      assessmentType: d.assessmentType,
      questionnaires: d.questionnaires.length ? JSON.stringify(d.questionnaires) : null,
      signs: JSON.stringify(this.getDefaultTemplateSigns()),
    };
  }

  /** The backend-seeded default template from a loaded list, if present. */
  findDefaultTemplate(templates: DaySheetTemplate[]): DaySheetTemplate | null {
    return templates.find(t => t.name === DaySheetTemplateService.DEFAULT_TEMPLATE_NAME) || null;
  }

  listTemplates(): Observable<DaySheetTemplate[]> {
    return this.appSyncService.hydrated().pipe(
      switchMap(client =>
        client.query<ListDaySheetTemplatesByClinicSetupIdQuery>({
          query: gql(listDaySheetTemplatesByClinicSetupId),
          variables: { clinicSetupId: this.clinicSetupService.clinicSetupId },
          fetchPolicy: 'network-only',
        })
      ),
      map((result: ApolloQueryResult<ListDaySheetTemplatesByClinicSetupIdQuery>) => {
        const items =
          (result.data.listDaySheetTemplatesByClinicSetupId &&
            result.data.listDaySheetTemplatesByClinicSetupId.items) ||
          [];
        return items.filter((t): t is DaySheetTemplate => t !== null);
      }),
      switchMap(templates => {
        if (templates.length > 0) {
          return this.migrateOldDefaults(templates).pipe(
            tap(migrated => this.markDefaultIds(migrated))
          );
        }
        return this.seedDefaultsOnce();
      })
    );
  }

  getTemplate(id: string): Observable<DaySheetTemplate | null> {
    return this.appSyncService.hydrated().pipe(
      switchMap(client =>
        client.query({
          query: gql(getDaySheetTemplateById),
          variables: { id },
          fetchPolicy: 'cache-first',
        })
      ),
      map((result: ApolloQueryResult<{ getDaySheetTemplate: DaySheetTemplate | null }>) =>
        result.data.getDaySheetTemplate
      )
    );
  }

  createTemplate(
    name: string,
    questionnaires: string[],
    assessmentType: string | null,
    signs: TemplateSignConfig[]
  ): Observable<DaySheetTemplate> {
    const input: CreateDaySheetTemplateInput = {
      daySheetTemplateClinicSetupId: this.clinicSetupService.clinicSetupId,
      daySheetTemplateClinicId: this.clinicSetupService.clinicId,
      name,
      questionnaires: questionnaires.length ? JSON.stringify(questionnaires) : null,
      assessmentType,
      signs: signs.length ? JSON.stringify(signs) : null,
    };
    return this.appSyncService.hydrated().pipe(
      switchMap(client => client.mutate({ mutation: gql(createDaySheetTemplate), variables: { input } })),
      map((result: ApolloQueryResult<{ createDaySheetTemplate: DaySheetTemplate }>) =>
        result.data.createDaySheetTemplate
      )
    );
  }

  updateTemplate(
    id: string,
    name: string,
    questionnaires: string[],
    assessmentType: string | null,
    signs: TemplateSignConfig[]
  ): Observable<DaySheetTemplate> {
    const input: UpdateDaySheetTemplateInput = {
      id,
      name,
      questionnaires: questionnaires.length ? JSON.stringify(questionnaires) : null,
      assessmentType,
      signs: signs.length ? JSON.stringify(signs) : null,
    };
    return this.appSyncService.hydrated().pipe(
      switchMap(client => client.mutate({ mutation: gql(updateDaySheetTemplate), variables: { input } })),
      map((result: ApolloQueryResult<{ updateDaySheetTemplate: DaySheetTemplate }>) =>
        result.data.updateDaySheetTemplate
      )
    );
  }

  deleteTemplate(id: string): Observable<void> {
    return this.appSyncService.hydrated().pipe(
      switchMap(client => client.mutate({
        mutation: gql(`mutation DeleteDaySheetTemplate($input: DeleteDaySheetTemplateInput!) {
          deleteDaySheetTemplate(input: $input) { id }
        }`),
        variables: { input: { id } },
      })),
      map(() => undefined)
    );
  }

  parseQuestionnaires(template: DaySheetTemplate): string[] {
    if (!template.questionnaires) { return []; }
    try {
      const parsed = JSON.parse(template.questionnaires);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  getTemplateSigns(template: DaySheetTemplate | null | undefined): DaySheetTemplateSign[] {
    if (!template || !template.signs) { return []; }
    try {
      const parsed = JSON.parse(template.signs);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  // ─── Private ─────────────────────────────────────────────────────────────────

  private migrateOldDefaults(templates: DaySheetTemplate[]): Observable<DaySheetTemplate[]> {
    const toMigrate = templates.filter(t => t.id && t.name && OLD_DEFAULT_MIGRATION[t.name]);
    if (!toMigrate.length) {
      return of(templates);
    }
    const updates = toMigrate.map(t => {
      const migration = OLD_DEFAULT_MIGRATION[t.name!];
      const existingSigns = this.getTemplateSigns(t) as TemplateSignConfig[];
      const existingQuestionnaires = this.parseQuestionnaires(t);
      return this.updateTemplate(t.id!, migration.name, existingQuestionnaires, migration.assessmentType, existingSigns).pipe(
        catchError(() => of({ ...t, name: migration.name, assessmentType: migration.assessmentType as any }))
      );
    });
    return forkJoin(updates).pipe(
      map(updated => {
        const updatedMap = new Map(
          updated
            .filter((u): u is DaySheetTemplate => u != null && !!u.id)
            .map(u => [u.id!, u])
        );
        return templates.map(t => (t.id && updatedMap.get(t.id)) || t);
      }),
      catchError(() => of(templates))
    );
  }

  private markDefaultIds(templates: DaySheetTemplate[]): void {
    this._defaultTemplateIds = new Set(
      templates
        .filter(t => t.name && DEFAULT_TEMPLATE_NAMES.has(t.name))
        .map(t => t.id as string)
    );
  }

  /**
   * Seed defaults at most once per session. listTemplates() runs from both the day
   * sheets page and the add-patients modal; without this guard a brand-new account
   * could fire two concurrent seeds and end up with duplicate templates.
   */
  private seedDefaultsOnce(): Observable<DaySheetTemplate[]> {
    if (!this.seed$) {
      this.seed$ = this.seedDefaults().pipe(
        // Allow a retry on a later call if seeding failed (network/auth hiccup).
        tap({ error: () => { this.seed$ = null; } }),
        shareReplay(1)
      );
    }
    return this.seed$;
  }

  // PHASE1_SIMPLIFY: seed only the single fixed default template. One create (vs. a
  // forkJoin over many) is faster and removes the partial-failure window where a new
  // account could be left with no usable template.
  private seedDefaults(): Observable<DaySheetTemplate[]> {
    const d = this.defaultRaw;
    return this.createTemplate(
      d.name,
      d.questionnaires,
      d.assessmentType,
      this.getDefaultTemplateSigns()
    ).pipe(
      map(template => [template]),
      tap(templates => {
        this._defaultTemplateIds = new Set(templates.map(t => t.id as string));
      })
    );
  }
}
